import { AdminHeader } from "@/components/AdminHeader";
import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { CalendarX2, Trash2, Pencil, Layers, BarChart3, TrendingDown, TrendingUp, Download, Printer, Hourglass } from "lucide-react";

const REASON_PRESETS = ["Hurricane", "Maintenance", "Insurance renewal", "Sold", "Detailing"];
const CARD = "bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-6";

function Sparkline({ points }) {
  if (!points || points.length < 2 || points.every((p) => p.days === 0))
    return <span className="text-[10px] text-muted-foreground font-ui">new this year</span>;
  const max = Math.max(...points.map((p) => p.days), 1);
  const w = 96, h = 22, pad = 3;
  const stepX = (w - pad * 2) / (points.length - 1);
  const rising = points[points.length - 1].days > points[0].days;
  const color = rising ? "#C71E2E" : "#059669";
  const y = (d) => h - pad - (d / max) * (h - pad * 2);
  const coords = points.map((p, i) => `${pad + i * stepX},${y(p.days)}`).join(" ");
  return (
    <span className="flex items-center gap-2 mt-1">
      <svg width={w} height={h} data-testid="reason-sparkline-svg" className="overflow-visible shrink-0">
        <polyline points={coords} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        {points.map((p, i) => (
          <circle key={p.year} cx={pad + i * stepX} cy={y(p.days)} r="1.8" fill={color} />
        ))}
      </svg>
      <span className="text-[9px] font-ui text-muted-foreground whitespace-nowrap">
        {points.map((p) => `'${String(p.year).slice(2)}: ${p.days}d`).join("  ")}
      </span>
    </span>
  );
}

export default function BlackoutsTab() {
  const [blackouts, setBlackouts] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState({ vehicle_id: "all", start_date: "", end_date: "", reason: "" });
  const [singlePreset, setSinglePreset] = useState("");
  const [saving, setSaving] = useState(false);
  const [bulkRange, setBulkRange] = useState(null);
  const [bulkVehicles, setBulkVehicles] = useState([]);
  const [bulkReason, setBulkReason] = useState("");
  const [bulkPreset, setBulkPreset] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [editingReason, setEditingReason] = useState(null);
  const [reasonDraft, setReasonDraft] = useState("");
  const [selYear, setSelYear] = useState(new Date().getFullYear());
  const [buffer, setBuffer] = useState(0);
  const [bufferSaving, setBufferSaving] = useState(false);

  const load = () => {
    api.get("/admin/blackouts").then((r) => setBlackouts(r.data)).catch(() => {});
    api.get("/admin/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/admin/settings/turnover-buffer").then((r) => setBuffer(r.data.buffer_days ?? 0)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.start_date || !form.end_date) return toast.error("Select start and end dates");
    setSaving(true);
    try {
      await api.post("/admin/blackouts", {
        vehicle_id: form.vehicle_id === "all" ? null : form.vehicle_id,
        start_date: form.start_date,
        end_date: form.end_date,
        reason: form.reason,
      });
      toast.success("Blackout added");
      setForm({ vehicle_id: "all", start_date: "", end_date: "", reason: "" });
      setSinglePreset("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const editDates = async (b) => {
    const s = window.prompt(`Blackout for ${vehicleName(b.vehicle_id)} — new START date (YYYY-MM-DD):`, b.start_date);
    if (!s) return;
    const e = window.prompt("New END date (YYYY-MM-DD):", b.end_date);
    if (!e) return;
    try {
      await api.patch(`/admin/blackouts/${b.id}`, { start_date: s, end_date: e });
      toast.success("Blackout dates updated — availability recalculated everywhere");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/blackouts/${id}`);
      toast.success("Blackout removed");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const saveReason = async (id) => {
    try {
      await api.patch(`/admin/blackouts/${id}`, { reason: reasonDraft.trim() });
      toast.success("Reason updated");
      setEditingReason(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const vehicleName = (id) => (id ? vehicles.find((v) => v.id === id)?.name || "Vehicle" : "All Vehicles");

  const saveBuffer = async (days) => {
    setBuffer(days);
    setBufferSaving(true);
    try {
      const { data } = await api.put("/admin/settings/turnover-buffer", { buffer_days: days });
      toast.success(days === 0 ? "Turnover buffer off — auto-blackouts back to rental dates only" : `Turnover buffer set: ${days} day${days > 1 ? "s" : ""} after every rental — ${data.blackouts_recalculated} booking blackout${data.blackouts_recalculated === 1 ? "" : "s"} updated`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBufferSaving(false);
    }
  };

  const allSelected = vehicles.length > 0 && bulkVehicles.length === vehicles.length;
  const toggleVehicle = (id) =>
    setBulkVehicles((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const toggleAll = () => setBulkVehicles(allSelected ? [] : vehicles.map((v) => v.id));

  const bulkSave = async () => {
    if (!bulkRange?.from || !bulkRange?.to) return toast.error("Pick a date range on the calendar");
    if (bulkVehicles.length === 0) return toast.error("Select at least one vehicle — or the whole fleet");
    setBulkSaving(true);
    try {
      const { data } = await api.post("/admin/blackouts/bulk", {
        vehicle_ids: allSelected ? [] : bulkVehicles,
        start_date: format(bulkRange.from, "yyyy-MM-dd"),
        end_date: format(bulkRange.to, "yyyy-MM-dd"),
        reason: bulkReason,
      });
      toast.success(allSelected ? "Fleet-wide blackout added" : `${data.created} vehicles blocked for the range`);
      setBulkRange(null);
      setBulkVehicles([]);
      setBulkReason("");
      setBulkPreset("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBulkSaving(false);
    }
  };

  const yearNow = new Date().getFullYear();

  const reasonYearMatrix = useMemo(() => {
    const m = {};
    const rateOf = (vid) => vehicles.find((v) => v.id === vid)?.price_per_day || 0;
    const active = vehicles.filter((v) => v.active !== false);
    const fleetRate = active.reduce((s, v) => s + (v.price_per_day || 0), 0);
    const fleetSize = active.length || 1;
    blackouts.forEach((b) => {
      const s = new Date(b.start_date + "T00:00:00");
      const e = new Date(b.end_date + "T00:00:00");
      const days = Math.max(1, Math.round((e - s) / 86400000));
      const daysCount = b.vehicle_id ? days : days * fleetSize;
      const lost = b.vehicle_id ? days * rateOf(b.vehicle_id) : days * fleetRate;
      const key = (b.reason || "").trim() || "No reason given";
      const y = s.getFullYear();
      if (!m[key]) m[key] = {};
      if (!m[key][y]) m[key][y] = { days: 0, lost: 0 };
      m[key][y].days += daysCount;
      m[key][y].lost += lost;
    });
    return m;
  }, [blackouts, vehicles]);

  const yearlyLost = useMemo(() => {
    const perYear = {};
    Object.values(reasonYearMatrix).forEach((byYear) =>
      Object.entries(byYear).forEach(([y, cell]) => {
        perYear[y] = (perYear[y] || 0) + cell.lost;
      })
    );
    return perYear;
  }, [reasonYearMatrix]);

  const years = useMemo(() => {
    const set = new Set([yearNow, ...Object.keys(yearlyLost).map(Number)]);
    return [...set].sort((a, b) => b - a);
  }, [yearlyLost, yearNow]);

  const thisYearLost = yearlyLost[selYear] || 0;
  const lastYearLost = yearlyLost[selYear - 1] || 0;
  const yoyPct = lastYearLost > 0 ? Math.round(((thisYearLost - lastYearLost) / lastYearLost) * 100) : null;

  const reasonStats = useMemo(() => {
    return Object.entries(reasonYearMatrix)
      .map(([reason, byYear]) => [reason, byYear[selYear] || { days: 0, lost: 0 }])
      .filter(([, v]) => v.lost > 0 || v.days > 0)
      .sort((a, b) => b[1].lost - a[1].lost);
  }, [reasonYearMatrix, selYear]);
  const maxReasonLost = Math.max(...reasonStats.map(([, v]) => v.lost), 1);

  const reportData = useMemo(() => {
    const rows = [];
    const yearTotals = {};
    Object.entries(reasonYearMatrix).forEach(([reason, byYear]) => {
      Object.entries(byYear).forEach(([y, cell]) => {
        rows.push({ reason, year: Number(y), days: cell.days, lost: cell.lost });
        if (!yearTotals[y]) yearTotals[y] = { days: 0, lost: 0 };
        yearTotals[y].days += cell.days;
        yearTotals[y].lost += cell.lost;
      });
    });
    rows.sort((a, b) => a.year - b.year || b.lost - a.lost);
    return { rows, yearTotals };
  }, [reasonYearMatrix]);

  const exportCSV = () => {
    const lines = ["Reason,Year,Blocked Days,Est. Revenue Lost (USD)"];
    reportData.rows.forEach((r) => lines.push(`"${r.reason}",${r.year},${r.days},${r.lost.toFixed(2)}`));
    Object.keys(reportData.yearTotals).sort().forEach((y) => {
      const t = reportData.yearTotals[y];
      lines.push(`"TOTAL ${y}",${y},${t.days},${t.lost.toFixed(2)}`);
    });
    const gDays = reportData.rows.reduce((s, r) => s + r.days, 0);
    const gLost = reportData.rows.reduce((s, r) => s + r.lost, 0);
    lines.push(`"GRAND TOTAL","",${gDays},${gLost.toFixed(2)}`);
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `easydrive-downtime-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Downtime CSV downloaded");
  };

  return (
    <div data-testid="admin-blackouts-tab" className="space-y-8">
      <AdminHeader testid="admin-header-blackouts" eyebrow="Fleet" title="Blackout Dates" description="Block vehicles for maintenance, hurricanes or private use." className="" />

      <div data-testid="turnover-buffer-card" className={`${CARD} flex flex-wrap items-center justify-between gap-4`}>
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-10 h-10 rounded-full bg-[#C71E2E]/10 flex items-center justify-center shrink-0"><Hourglass size={17} className="text-[#C71E2E]" /></span>
          <div>
            <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A]">Turnover buffer</p>
            <p className="text-xs text-muted-foreground font-ui mt-0.5">Auto-block cleaning/inspection days after every confirmed rental. Saving updates all current bookings.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {[0, 1, 2, 3].map((d) => (
            <button
              key={d}
              data-testid={`turnover-buffer-${d}`}
              onClick={() => saveBuffer(d)}
              disabled={bufferSaving}
              className={`rounded-full px-4 h-9 text-sm font-ui transition-colors duration-200 disabled:opacity-50 ${buffer === d ? "bg-[#C71E2E] text-white" : "border border-[#E5DED5] text-foreground/70 hover:border-[#C71E2E]/50 hover:text-[#C71E2E]"}`}
            >
              {d === 0 ? "Off" : `${d}d`}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={`${CARD} h-fit`}>
          <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
            <CalendarX2 size={14} className="text-[#C71E2E]" /> New Blackout
          </p>
          <div className="mt-5 space-y-4">
            <div>
              <Label className="font-ui text-xs text-muted-foreground">Vehicle</Label>
              <Select value={form.vehicle_id} onValueChange={(val) => setForm({ ...form, vehicle_id: val })}>
                <SelectTrigger data-testid="blackout-vehicle-select" className="input-dark mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-[#E5DED5]">
                  <SelectItem value="all">All Vehicles (fleet-wide)</SelectItem>
                  {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-ui text-xs text-muted-foreground">Start date</Label>
              <Input data-testid="blackout-start-date" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="input-dark mt-1.5" />
            </div>
            <div>
              <Label className="font-ui text-xs text-muted-foreground">End date (last blocked day)</Label>
              <Input data-testid="blackout-end-date" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="input-dark mt-1.5" />
            </div>
            <div>
              <Label className="font-ui text-xs text-muted-foreground">Reason</Label>
              <Select value={singlePreset} onValueChange={(val) => { setSinglePreset(val); setForm({ ...form, reason: val === "custom" ? "" : val }); }}>
                <SelectTrigger data-testid="blackout-reason-preset" className="input-dark mt-1.5"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
                <SelectContent className="bg-white border-[#E5DED5]">
                  {REASON_PRESETS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  <SelectItem value="custom">Custom…</SelectItem>
                </SelectContent>
              </Select>
              {singlePreset === "custom" && (
                <Input data-testid="blackout-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Private event…" className="input-dark mt-2" />
              )}
            </div>
            <Button data-testid="blackout-submit-button" onClick={save} disabled={saving} className="w-full rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui hover:scale-95 transition-transform">
              {saving ? "Saving…" : "Add Blackout"}
            </Button>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          {blackouts.length === 0 ? (
            <p data-testid="blackouts-empty" className="text-muted-foreground font-ui">No blackout dates set.</p>
          ) : (
            blackouts.map((b) => (
              <div key={b.id} data-testid={`blackout-row-${b.id}`} className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-5 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-ui font-medium text-[#1A1A1A]">{vehicleName(b.vehicle_id)}</p>
                  <p className="text-sm text-muted-foreground font-ui mt-1">
                    {fmtDate(b.start_date)} → {fmtDate(b.end_date)}
                  </p>
                  {editingReason === b.id ? (
                    <div className="mt-2 flex items-center gap-2">
                      <Input
                        data-testid={`blackout-reason-input-${b.id}`}
                        value={reasonDraft}
                        onChange={(e) => setReasonDraft(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && saveReason(b.id)}
                        placeholder="Reason…"
                        className="input-dark h-8 text-xs w-56"
                        autoFocus
                      />
                      <Button data-testid={`blackout-reason-save-${b.id}`} size="sm" onClick={() => saveReason(b.id)} className="rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui h-8 px-3 text-xs">Save</Button>
                      <button data-testid={`blackout-reason-cancel-${b.id}`} onClick={() => setEditingReason(null)} className="text-xs font-ui text-muted-foreground hover:text-foreground transition-colors duration-200">Cancel</button>
                    </div>
                  ) : (
                    <button
                      data-testid={`blackout-reason-chip-${b.id}`}
                      onClick={() => { setEditingReason(b.id); setReasonDraft(b.reason || ""); }}
                      title="Click to edit reason"
                      className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-ui transition-colors duration-200 ${b.reason ? "border-[#C71E2E]/30 text-foreground/80 hover:border-[#C71E2E] hover:text-[#C71E2E]" : "border-dashed border-[#E5DED5] text-muted-foreground hover:border-[#C71E2E]/50 hover:text-[#C71E2E]"}`}
                    >
                      {b.reason || "+ Add reason"}
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2.5">
                  <button data-testid={`blackout-edit-button-${b.id}`} onClick={() => editDates(b)} title="Change blackout dates" className="text-muted-foreground hover:text-[#C71E2E] transition-colors duration-200" aria-label="Edit blackout dates">
                    <Pencil size={15} />
                  </button>
                  <button data-testid={`blackout-delete-button-${b.id}`} onClick={() => remove(b.id)} className="text-muted-foreground hover:text-red-700 transition-colors duration-200" aria-label="Delete blackout">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div data-testid="reason-stats-card" className={CARD}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
            <BarChart3 size={14} className="text-[#C71E2E]" /> Downtime cost by reason
          </p>
          <div className="flex items-center gap-2">
            <button data-testid="downtime-csv-button" onClick={exportCSV} title="Download reason × year × dollars CSV" className="h-8 inline-flex items-center gap-1.5 rounded-lg border border-[#E5DED5] bg-white px-3 text-xs font-ui text-[#4A4A4A] hover:border-[#C71E2E]/50 hover:text-[#C71E2E] transition-colors duration-200">
              <Download size={13} /> CSV
            </button>
            <button data-testid="downtime-print-button" onClick={() => window.print()} title="Print branded downtime report" className="h-8 inline-flex items-center gap-1.5 rounded-lg border border-[#E5DED5] bg-white px-3 text-xs font-ui text-[#4A4A4A] hover:border-[#C71E2E]/50 hover:text-[#C71E2E] transition-colors duration-200">
              <Printer size={13} /> Print Report
            </button>
            <select
              data-testid="downtime-year-select"
              value={selYear}
              onChange={(e) => setSelYear(Number(e.target.value))}
              className="h-8 rounded-lg border border-[#E5DED5] bg-white px-2.5 text-xs font-ui hover:border-[#C71E2E]/50 transition-colors duration-200"
            >
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
        <div data-testid="downtime-yoy" className="mt-3 flex flex-wrap items-baseline gap-2.5">
          <p className="font-display text-2xl tracking-tight text-[#1A1A1A]">${Math.round(thisYearLost).toLocaleString("en-US")} downtime in {selYear}</p>
          {yoyPct !== null ? (
            <p data-testid="downtime-yoy-delta" className={`inline-flex items-center gap-1 text-sm font-ui font-semibold ${thisYearLost <= lastYearLost ? "text-emerald-600" : "text-red-700"}`}>
              {thisYearLost <= lastYearLost ? <TrendingDown size={14} /> : <TrendingUp size={14} />}
              {Math.abs(yoyPct)}% vs {selYear - 1}
            </p>
          ) : (
            <p className="text-xs font-ui text-muted-foreground">no {selYear - 1} data to compare</p>
          )}
        </div>
        {reasonStats.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground font-ui">No blocked days in {selYear} — pick another year above to compare.</p>
        ) : (
          <div className="mt-5 space-y-4">
            {reasonStats.map(([reason, v]) => (
              <div key={reason} data-testid={`reason-stat-${reason.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="flex items-center gap-4">
                <div className="w-48 shrink-0">
                  <p className="text-sm font-ui truncate text-[#1A1A1A]">{reason}</p>
                  <Sparkline points={years.map((y) => ({ year: y, days: (reasonYearMatrix[reason] || {})[y]?.days || 0 }))} />
                </div>
                <div className="flex-1 h-2.5 rounded-full bg-[#E5DED5] overflow-hidden self-center">
                  <div className="h-full rounded-full bg-[#C71E2E]/70" style={{ width: `${(v.lost / maxReasonLost) * 100}%` }} />
                </div>
                <p className="w-28 text-right text-sm font-ui text-[#C71E2E] font-semibold self-center">{"$" + Math.round(v.lost).toLocaleString("en-US")}</p>
                <p className="w-14 text-right text-xs font-ui text-muted-foreground self-center">{v.days}d</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div data-testid="bulk-blackout-card" className={`${CARD} ring-1 ring-[#C71E2E]/20`}>
        <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
          <Layers size={14} className="text-[#C71E2E]" /> Bulk block for maintenance — knock out multiple cars in two clicks
        </p>
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="w-fit">
            <Calendar
              data-testid="bulk-blackout-calendar"
              mode="range"
              selected={bulkRange}
              onSelect={setBulkRange}
              numberOfMonths={2}
              disabled={{ before: new Date() }}
              className="bg-white border border-[#E5DED5] rounded-xl"
            />
            <p data-testid="bulk-range-label" className="mt-3 text-xs font-ui text-muted-foreground">
              {bulkRange?.from && bulkRange?.to
                ? `${format(bulkRange.from, "MMM d, yyyy")} → ${format(bulkRange.to, "MMM d, yyyy")}`
                : "Click a start date, then an end date"}
            </p>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="font-ui text-xs text-muted-foreground">Vehicles to block</Label>
              <button data-testid="bulk-select-all" onClick={toggleAll} className="text-xs font-ui text-[#C71E2E] hover:underline">
                {allSelected ? "Clear selection" : "Select entire fleet"}
              </button>
            </div>
            <div className="mt-2.5 flex flex-wrap gap-2">
              {vehicles.map((v) => (
                <button
                  key={v.id}
                  data-testid={`bulk-vehicle-chip-${v.id}`}
                  onClick={() => toggleVehicle(v.id)}
                  className={`rounded-full border px-4 py-2 text-xs font-ui transition-all duration-200 ${
                    bulkVehicles.includes(v.id)
                      ? "bg-[#C71E2E] text-white border-[#C71E2E] shadow-[0_4px_14px_rgba(199,30,46,0.3)]"
                      : "bg-white border-[#E5DED5] text-muted-foreground hover:border-[#C71E2E]/50 hover:text-foreground"
                  }`}
                >
                  {v.name}
                </button>
              ))}
            </div>
            <div className="mt-5">
              <Label className="font-ui text-xs text-muted-foreground">Reason</Label>
              <Select value={bulkPreset} onValueChange={(val) => { setBulkPreset(val); setBulkReason(val === "custom" ? "" : val); }}>
                <SelectTrigger data-testid="bulk-reason-preset" className="input-dark mt-1.5"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
                <SelectContent className="bg-white border-[#E5DED5]">
                  {REASON_PRESETS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  <SelectItem value="custom">Custom…</SelectItem>
                </SelectContent>
              </Select>
              {bulkPreset === "custom" && (
                <Input data-testid="bulk-reason-input" value={bulkReason} onChange={(e) => setBulkReason(e.target.value)} placeholder="Fleet maintenance week, holiday shutdown…" className="input-dark mt-2" />
              )}
            </div>
            <Button data-testid="bulk-submit-button" onClick={bulkSave} disabled={bulkSaving} className="mt-5 w-full rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui hover:scale-95 transition-transform">
              {bulkSaving
                ? "Blocking…"
                : allSelected && bulkVehicles.length > 0
                ? "Block Entire Fleet"
                : `Block ${bulkVehicles.length || ""} ${bulkVehicles.length === 1 ? "Vehicle" : "Vehicles"}`}
            </Button>
          </div>
        </div>
      </div>

      <div className="print-report" data-testid="downtime-print-report">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <img src="/logo.png" alt="Easy Drive Car Rental" style={{ height: 64 }} />
          <div style={{ textAlign: "right", fontSize: 11, color: "#555", lineHeight: 1.7 }}>
            <p>West Bay Street</p>
            <p>Nassau, Bahamas</p>
            <p>+1 (242) 813-1775</p>
            <p>bookings@easydrivecarrental.com</p>
            <p>www.easydrivecarrental.com</p>
          </div>
        </div>
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: 26, marginTop: 24, color: "#171717" }}>Fleet Downtime Report</h1>
        <p style={{ fontSize: 12, color: "#555", marginTop: 4 }}>
          Generated {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
          {reportData.rows.length > 0 && ` · Period covered: ${Math.min(...reportData.rows.map((r) => r.year))} – ${Math.max(...reportData.rows.map((r) => r.year))}`}
        </p>
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 20, fontSize: 12 }}>
          <thead>
            <tr>
              {["Reason", "Year", "Blocked Days", "Est. Revenue Lost"].map((h) => (
                <th key={h} style={{ textAlign: "left", borderBottom: "2px solid #C71E2E", padding: "8px 6px", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "#4A4A4A" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reportData.rows.map((r, i) => (
              <tr key={i}>
                <td style={{ borderBottom: "1px solid #E5DED5", padding: "7px 6px" }}>{r.reason}</td>
                <td style={{ borderBottom: "1px solid #E5DED5", padding: "7px 6px" }}>{r.year}</td>
                <td style={{ borderBottom: "1px solid #E5DED5", padding: "7px 6px" }}>{r.days}</td>
                <td style={{ borderBottom: "1px solid #E5DED5", padding: "7px 6px" }}>${r.lost.toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
              </tr>
            ))}
            {Object.keys(reportData.yearTotals).sort().map((y) => (
              <tr key={y}>
                <td style={{ padding: "9px 6px", fontWeight: "bold" }}>TOTAL</td>
                <td style={{ padding: "9px 6px", fontWeight: "bold" }}>{y}</td>
                <td style={{ padding: "9px 6px", fontWeight: "bold" }}>{reportData.yearTotals[y].days}</td>
                <td style={{ padding: "9px 6px", fontWeight: "bold", color: "#C71E2E" }}>${reportData.yearTotals[y].lost.toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ fontSize: 10, color: "#777", marginTop: 24, lineHeight: 1.6 }}>
          Estimate based on blocked days × daily rental rate at time of blocking. Fleet-wide blocks count every active vehicle.
          Prepared for insurance and quarterly review purposes.
        </p>
      </div>
    </div>
  );
}
